/**
 * Internationalization configuration.
 *
 * Defines supported locales and helpers for locale handling.
 * Locale format: {country}-{language} (e.g., 'us-en', 'de-de')
 */

import { localeConfig } from '@/lib/configuration/configuration';

// Get all valid locales from the configuration
export const locales = localeConfig.getAllLocales();

// Alias for backwards compatibility
export const SUPPORTED_LOCALES = locales;

// Default locale for fallback
export const defaultLocale = 'us-en';

// Type for valid locales
export type Locale = string;

/**
 * Extract language code from locale.
 *
 * @example
 * getLanguageFromLocale('us-en') // 'en'
 * getLanguageFromLocale('de-de') // 'de'
 * getLanguageFromLocale('jp-jp') // 'jp' -> maps to 'ja' for translations
 */
export function getLanguageFromLocale(locale: string): string {
  const [, lang] = locale.split('-');

  // Map language slugs to translation file names
  // Some slugs differ from ISO codes (e.g., 'jp' for Japanese)
  const langMap: Record<string, string> = {
    jp: 'ja', // Japanese
    zht: 'zht', // Traditional Chinese
  };

  return langMap[lang] || lang || 'en';
}

/**
 * Map an app locale to a Google reCAPTCHA language code (`hl`).
 *
 * Google renders and translates the reCAPTCHA widget itself; the only thing
 * we control is the `hl` parameter. The supported codes differ from our URL
 * slugs (e.g. Chinese must be `zh-CN`/`zh-TW`, not `zh`/`zht`), so we map them
 * here. Unknown languages fall through to the bare language code, which lets
 * Google handle/ignore it gracefully.
 *
 * @see https://developers.google.com/recaptcha/docs/language
 *
 * @example
 * getRecaptchaLanguageFromLocale('us-en')  // 'en'
 * getRecaptchaLanguageFromLocale('jp-jp')  // 'ja'
 * getRecaptchaLanguageFromLocale('tw-zht') // 'zh-TW'
 */
export function getRecaptchaLanguageFromLocale(locale: string): string {
  // getLanguageFromLocale already normalises slugs like 'jp' -> 'ja'.
  const lang = getLanguageFromLocale(locale);

  // Remaining slugs that differ from Google's expected codes.
  const recaptchaLangMap: Record<string, string> = {
    zh: 'zh-CN', // Simplified Chinese
    zht: 'zh-TW', // Traditional Chinese
    pt: 'pt-PT', // Portuguese
  };

  return recaptchaLangMap[lang] || lang;
}

/**
 * Extract country code from locale.
 *
 * @example
 * getCountryFromLocale('us-en') // 'us'
 * getCountryFromLocale('de-de') // 'de'
 *
 * @TODO: Refactor this to use the localeConfig instead of splitting the locale string (maybe ?)
 */
export function getCountryFromLocale(locale: string): string {
  const [country] = locale.split('-');
  return country || 'us';
}

/**
 * Check if a locale string is valid.
 */
export function isValidLocale(locale: string): boolean {
  return localeConfig.isValidLocale(locale);
}

/**
 * Get all supported languages (unique).
 */
export function getSupportedLanguages(): string[] {
  const languages = new Set<string>();

  for (const locale of locales) {
    languages.add(getLanguageFromLocale(locale));
  }

  return Array.from(languages);
}
